import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { ArrowRight, X } from 'lucide-react';
import axios from 'utils/axios';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { normalizeMediaUrl } from 'utils/getImageUrl';

// Import custom icons - matching sidebar icons
import expertIcon from 'assets/icons/laurel-wreath-01.svg';
import medicationIcon from 'assets/icons/medicine-02.svg';
import newsIcon from 'assets/icons/sticky-note-01.svg'; // Veille icon from sidebar
import opinionIcon from 'assets/icons/avis.svg';
import institutionIcon from 'assets/icons/court-house.svg';
import searchIcon from 'assets/icons/search-01.svg';
import formationIcon from 'assets/icons/playlist-01_w.svg'; // Formation icon from sidebar
import masterclassIcon from 'assets/icons/co-present.svg'; // Masterclass icon from sidebar
import diseaseIcon from 'assets/icons/diseases.svg';
import eventIcon from 'assets/icons/calendar-03.svg';
import moduleIcon from 'assets/icons/play-list_w.svg'; // Module icon from sidebar
import libraryIcon from 'assets/icons/document-attachment.svg';

// Import default images
import { expertImages } from 'dataClient/expert';
import { institutionImages } from 'dataClient/institutions';

const GlobalSearch = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const closeButtonRef = useRef(null);
  const isClosingRef = useRef(false);

  // Handler for close button
  const handleClose = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!isClosingRef.current) {
      isClosingRef.current = true;
      onClose();
      setTimeout(() => {
        isClosingRef.current = false;
      }, 300);
    }
  }, [onClose]);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({
    experts: [],
    medications: [],
    news: [],
    opinions: [],
    institutions: [],
    formations: [],
    masterclasses: [],
    diseases: [],
    events: [],
    modules: [],
    library: [],
  });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allResults, setAllResults] = useState([]);
  const [, setSearchedApis] = useState([]);
  const abortControllerRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const selectedItemRef = useRef(null);

  // Create debounced search function with increased delay for stability
  const debouncedSearch = useRef(
    debounce(async (term) => {
      const cleanedTerm = (term || '').trim();

      if (!cleanedTerm || cleanedTerm.length < 2) {
        // Batch state updates to prevent flickering
        setLoading(false);
        setResults({
          experts: [],
          medications: [],
          news: [],
          opinions: [],
          institutions: [],
          formations: [],
          masterclasses: [],
          diseases: [],
          events: [],
          modules: [],
          library: [],
        });
        setAllResults([]);
        setSearchedApis([]);
        return;
      }

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Use requestAnimationFrame to batch loading state update
      requestAnimationFrame(() => {
        if (abortControllerRef.current === abortController) {
      setLoading(true);
        }
      });
      
      try {
        // Search all content types in parallel
        const [
          expertsRes,
          medicationsRes,
          newsRes,
          opinionsRes,
          institutionsRes,
          formationsRes,
          masterclassesRes,
          diseasesRes,
          eventsRes,
          modulesRes,
          libraryRes,
        ] = await Promise.allSettled([
          axios.get('/api/experts/list/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          axios.get('/api/medications/list/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          axios.get('/api/monitoring/news/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          axios.get('/api/experts/opinions/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          axios.get('/api/institutions/list/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Formations
          axios.get('/api/formations/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Masterclasses
          axios.get('/api/formations/masterclass/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Diseases / pathologies
          axios.get('/api/disease/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Events
          axios.get('/api/monitoring/events/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Modules
          axios.get('/api/modules/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
          // Library (documents)
          axios.get('/api/library/list/', {
            params: { search: cleanedTerm },
            signal: abortController.signal,
          }),
        ]);

        // Only process results if this is still the current request
        if (abortControllerRef.current !== abortController) {
          return;
        }

        const newResults = {
          experts: expertsRes.status === 'fulfilled' ? (expertsRes.value.data.results || expertsRes.value.data || []) : [],
          medications: medicationsRes.status === 'fulfilled' ? (medicationsRes.value.data.results || medicationsRes.value.data || []) : [],
          news: newsRes.status === 'fulfilled' ? (newsRes.value.data.results || newsRes.value.data || []) : [],
          opinions: opinionsRes.status === 'fulfilled' ? (opinionsRes.value.data.results || opinionsRes.value.data || []) : [],
          institutions: institutionsRes.status === 'fulfilled' ? (institutionsRes.value.data.results || institutionsRes.value.data || []) : [],
          formations: formationsRes.status === 'fulfilled' ? (formationsRes.value.data.results || formationsRes.value.data || []) : [],
          masterclasses: masterclassesRes.status === 'fulfilled' ? (masterclassesRes.value.data.results || masterclassesRes.value.data || []) : [],
          diseases: diseasesRes.status === 'fulfilled' ? (diseasesRes.value.data.results || diseasesRes.value.data || []) : [],
          events: eventsRes.status === 'fulfilled' ? (eventsRes.value.data.results || eventsRes.value.data || []) : [],
          modules: modulesRes.status === 'fulfilled' ? (modulesRes.value.data.results || modulesRes.value.data || []) : [],
          library: libraryRes.status === 'fulfilled' ? (libraryRes.value.data.results || libraryRes.value.data || []) : [],
        };

        // Flatten results for keyboard navigation
        const flattened = [
          ...newResults.experts.map(item => ({ ...item, type: 'expert' })),
          ...newResults.medications.map(item => ({ ...item, type: 'medication' })),
          ...newResults.news.map(item => ({ ...item, type: 'news' })),
          ...newResults.opinions.map(item => ({ ...item, type: 'opinion' })),
          ...newResults.institutions.map(item => ({ ...item, type: 'institution' })),
          ...newResults.formations.map(item => ({ ...item, type: 'formation' })),
          ...newResults.masterclasses.map(item => ({ ...item, type: 'masterclass' })),
          ...newResults.diseases.map(item => ({ ...item, type: 'disease' })),
          ...newResults.events.map(item => ({ ...item, type: 'event' })),
          ...newResults.modules.map(item => ({ ...item, type: 'module' })),
          ...newResults.library.map(item => ({ ...item, type: 'library' })),
        ];

        // Batch all state updates together to prevent flickering
        requestAnimationFrame(() => {
          if (abortControllerRef.current === abortController) {
            setResults(newResults);
        setAllResults(flattened);
        setSelectedIndex(0);
            setLoading(false);
          }
        });
      } catch (error) {
        // Ignore aborted requests
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return;
        }
        // Only update state if this is still the current request
        if (abortControllerRef.current === abortController) {
          requestAnimationFrame(() => {
            if (abortControllerRef.current === abortController) {
        setResults({
          experts: [],
          medications: [],
          news: [],
          opinions: [],
          institutions: [],
                formations: [],
                masterclasses: [],
                diseases: [],
                events: [],
                modules: [],
                library: [],
        });
        setAllResults([]);
          setLoading(false);
        }
          });
      }
    }
  }, 400)
  );

  // Wrapper function to call debounced search
  const performSearch = useCallback((term) => {
    debouncedSearch.current(term);
  }, []);

  useEffect(() => {
    const debouncedSearchInstance = debouncedSearch.current;
    if (open) {
      // Cancel any pending search requests first
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Cancel debounced search
      debouncedSearchInstance.cancel();
      
      // Reset state in a batched way to prevent flickering
      requestAnimationFrame(() => {
      setSearchTerm('');
        setResults({
          experts: [],
          medications: [],
          news: [],
          opinions: [],
          institutions: [],
          formations: [],
          masterclasses: [],
          diseases: [],
          events: [],
          modules: [],
          library: [],
        });
      setAllResults([]);
      setSearchedApis([]);
      setSelectedIndex(0);
        setLoading(false);
      });
      
      // Focus input when dialog opens - use requestAnimationFrame for smoother focus
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            // Prevent any visual glitches by ensuring cursor is at the end
            if (inputRef.current.setSelectionRange) {
              const length = inputRef.current.value.length;
              inputRef.current.setSelectionRange(length, length);
            }
          }
        }, 150);
      });
    } else {
      // Cancel any pending requests when dialog closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Cancel debounced search
      debouncedSearchInstance.cancel();
    }
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      debouncedSearchInstance.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // No separate touch listener: rely on onClick with touchAction: 'manipulation' for fast tap on mobile

  const handleSelectResult = useCallback((result) => {
    if (!result) return;

    let path = '';
    switch (result.type) {
      case 'expert':
        path = `/experts/${result.id}`;
        break;
      case 'medication':
        path = `/medicaments/${result.id}?from_search=1`;
        break;
      case 'news':
        path = `/veille/${result.id}`;
        break;
      case 'opinion':
        path = `/avis/${result.id}`;
        break;
      case 'institution':
        path = `/institutions/${result.id}`;
        break;
      case 'formation':
        path = `/formation/${result.id}`;
        break;
      case 'masterclass':
        path = `/masterclass/${result.id}`;
        break;
      case 'disease':
        path = `/pathologies/${result.id}`;
        break;
      case 'event':
        path = `/event/${result.id}`;
        break;
      case 'module':
        // Modules need parent formation/masterclass to navigate
        const parentType = result.parentType || (result.formation ? 'formation' : result.masterclass ? 'masterclass' : 'formation');
        const parentId = result.formation?.id || result.masterclass?.id || result.formation_id || result.masterclass_id;
        if (parentId) {
          path = `/${parentType}/${parentId}/modules`;
          // Store module info in state for navigation
          navigate(path, {
            state: {
              moduleId: result.id,
              moduleNumber: result.order || 1,
            }
          });
          onClose();
          setSearchTerm('');
          return;
        } else {
          // Fallback to modules list if no parent
          path = '/modules';
        }
        break;
      case 'library':
        path = `/library/${result.id}`;
        break;
      default:
        return;
    }

    navigate(path);
    onClose();
    setSearchTerm('');
  }, [navigate, onClose]);

  useEffect(() => {
    performSearch(searchTerm);
  }, [searchTerm, performSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Don't handle keyboard navigation if user is typing in the input
      if (e.target.tagName === 'INPUT' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && e.key !== 'Escape') {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const nextIndex = prev < allResults.length - 1 ? prev + 1 : prev;
          // Scroll to selected item with requestAnimationFrame for stability
          requestAnimationFrame(() => {
          setTimeout(() => {
            if (selectedItemRef.current) {
              selectedItemRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
          });
          return nextIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : 0;
          // Scroll to selected item with requestAnimationFrame for stability
          requestAnimationFrame(() => {
          setTimeout(() => {
            if (selectedItemRef.current) {
              selectedItemRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
          });
          return nextIndex;
        });
      } else if (e.key === 'Enter' && allResults.length > 0 && selectedIndex >= 0 && selectedIndex < allResults.length) {
        e.preventDefault();
        const selectedResult = allResults[selectedIndex];
        if (selectedResult) {
          handleSelectResult(selectedResult);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, allResults, selectedIndex, handleSelectResult, onClose]);

  const getResultTitle = (result) => {
    switch (result.type) {
      case 'expert':
        return `${result.title || ''} ${result.first_name || ''} ${result.last_name || ''}`.trim();
      case 'medication':
        return result.full_name || result.name || result.commercial_name || '';
      case 'news':
        return result.title || '';
      case 'opinion':
        return result.title || '';
      case 'institution':
        return result.name || result.abbreviation || '';
      case 'formation':
        return result.title || result.name || '';
      case 'masterclass':
        return result.title || result.name || '';
      case 'disease':
        return result.name || '';
      case 'event':
        return result.title || '';
      case 'module':
        return result.title || result.name || '';
      case 'library':
        return result.name || '';
      default:
        return '';
    }
  };

  const getResultSubtitle = (result) => {
    switch (result.type) {
      case 'expert':
        const professionName = typeof result.profession === 'object' && result.profession?.name 
          ? result.profession.name 
          : result.profession;
        const specialtyName = result.specialties?.[0] 
          ? (typeof result.specialties[0] === 'object' ? result.specialties[0].name : result.specialties[0])
          : '';
        return professionName || specialtyName || '';
      case 'medication':
        const activePrinciple = typeof result.active_principle === 'object' && result.active_principle?.name
          ? result.active_principle.name
          : result.active_principle;
        const therapeuticClass = typeof result.therapeutic_class === 'object' && result.therapeutic_class?.name
          ? result.therapeutic_class.name
          : result.therapeutic_class;
        return activePrinciple || therapeuticClass || '';
      case 'news':
        const categoryName = typeof result.category === 'object' && result.category?.name
          ? result.category.name
          : result.category;
        return categoryName || '';
      case 'opinion':
        return result.expert?.first_name && result.expert?.last_name
          ? `${result.expert.first_name} ${result.expert.last_name}`
          : '';
      case 'institution':
        return result.city || '';
      case 'formation': {
        const category =
          typeof result.category === 'object' && result.category?.name
            ? result.category.name
            : result.category;
        return category || result.level || '';
      }
      case 'masterclass': {
        const category =
          typeof result.category === 'object' && result.category?.name
            ? result.category.name
            : result.category;
        return category || result.level || '';
      }
      case 'disease': {
        const category =
          typeof result.category === 'object' && result.category?.name
            ? result.category.name
            : result.category;
        return category || '';
      }
      case 'event':
        return result.location_city || result.location_pays || '';
      case 'module':
        const formationName = result.formation?.title || result.formation?.name;
        const masterclassName = result.masterclass?.title || result.masterclass?.name;
        return formationName || masterclassName || '';
      case 'library': {
        const categoryName = typeof result.category === 'object' && result.category?.name
          ? result.category.name
          : result.category;
        return categoryName || result.file_type || '';
      }
      default:
        return '';
    }
  };

  const getResultImage = (result) => {
    let imageUrl = null;
    
    switch (result.type) {
      case 'expert':
        if (result.profile_image) {
          // Handle both full URLs and relative paths
          if (result.profile_image.startsWith('http')) {
            imageUrl = normalizeMediaUrl(result.profile_image);
          } else {
            const baseURL = import.meta.env.VITE_APP_API_URL || '';
            imageUrl = normalizeMediaUrl(baseURL + result.profile_image);
          }
        }
        break;
      case 'medication':
        const medImage = typeof result.image === 'string' ? result.image : (result.image?.url || null);
        imageUrl = medImage ? normalizeMediaUrl(medImage) : null;
        break;
      case 'institution':
        if (result.logo) {
          // Handle both full URLs and relative paths
          if (typeof result.logo === 'string') {
            if (result.logo.startsWith('http')) {
              imageUrl = normalizeMediaUrl(result.logo);
            } else {
              const baseURL = import.meta.env.VITE_APP_API_URL || '';
              imageUrl = normalizeMediaUrl(baseURL + result.logo);
            }
          } else if (result.logo?.url) {
            imageUrl = normalizeMediaUrl(result.logo.url);
          }
        }
        break;
      case 'opinion':
        // Use expert's profile image if available
        if (result.expert?.profile_image) {
          if (result.expert.profile_image.startsWith('http')) {
            imageUrl = normalizeMediaUrl(result.expert.profile_image);
          } else {
            const baseURL = import.meta.env.VITE_APP_API_URL || '';
            imageUrl = normalizeMediaUrl(baseURL + result.expert.profile_image);
          }
        }
        break;
      case 'news':
        // News might not have a direct image, but could have from sources
        const newsImage = result.image || result.source_image || null;
        imageUrl = newsImage ? normalizeMediaUrl(newsImage) : null;
        break;
      case 'module':
        const moduleImage = result.thumbnail || result.image || null;
        imageUrl = moduleImage ? normalizeMediaUrl(moduleImage) : null;
        break;
      case 'library':
        const libraryImage = result.thumbnail || result.organization_icon || null;
        imageUrl = libraryImage ? normalizeMediaUrl(libraryImage) : null;
        break;
      default:
        imageUrl = null;
    }
    
    return imageUrl;
  };

  const getDefaultImage = (result) => {
    switch (result.type) {
      case 'expert':
        // Use gender-based default image
        const gender = result.gender || 'M';
        const defaultImageIndex = gender === 'F' ? 17 : 11;
        return expertImages[defaultImageIndex];
      case 'institution':
        return institutionImages[7]; // Default institution image
      case 'opinion':
        // Use expert's gender for opinion default
        const expertGender = result.expert?.gender || 'M';
        const opinionDefaultIndex = expertGender === 'F' ? 17 : 11;
        return expertImages[opinionDefaultIndex];
      default:
        return null;
    }
  };

  const getResultIcon = (type) => {
    const iconStyle = { 
      width: 20, 
      height: 20,
      filter: 'brightness(0)',
    };
    switch (type) {
      case 'expert':
        return <Box component="img" src={expertIcon} alt="Expert" sx={iconStyle} />;
      case 'medication':
        return <Box component="img" src={medicationIcon} alt="Médicament" sx={iconStyle} />;
      case 'news':
        return <Box component="img" src={newsIcon} alt="Actualité" sx={iconStyle} />;
      case 'opinion':
        return <Box component="img" src={opinionIcon} alt="Avis" sx={iconStyle} />;
      case 'institution':
        return <Box component="img" src={institutionIcon} alt="Institution" sx={iconStyle} />;
      case 'formation':
        return <Box component="img" src={formationIcon} alt="Formation" sx={iconStyle} />;
      case 'masterclass':
        return <Box component="img" src={masterclassIcon} alt="Masterclass" sx={iconStyle} />;
      case 'disease':
        return <Box component="img" src={diseaseIcon} alt="Pathologie" sx={iconStyle} />;
      case 'event':
        return <Box component="img" src={eventIcon} alt="Événement" sx={iconStyle} />;
      case 'module':
        return <Box component="img" src={moduleIcon} alt="Module" sx={iconStyle} />;
      case 'library':
        return <Box component="img" src={libraryIcon} alt="Bibliothèque" sx={iconStyle} />;
      default:
        return <Box component="img" src={searchIcon} alt="Search" sx={iconStyle} />;
    }
  };

  const ResultImage = ({ result }) => {
    const [imageError, setImageError] = React.useState(false);
    const imageUrl = getResultImage(result);
    const defaultImage = getDefaultImage(result);
    const isCircular = result.type === 'expert' || result.type === 'opinion';
    
    // Determine which image to use
    const finalImageUrl = imageError || !imageUrl ? defaultImage : imageUrl;
    
    // If no default image available, fallback to icon
    if (!finalImageUrl) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: { xs: 48, sm: 56 }, 
          height: { xs: 48, sm: 56 },
          borderRadius: isCircular ? '50%' : 2,
          bgcolor: '#ffffff',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }}>
          {getResultIcon(result.type)}
        </Box>
      );
    }
    
    return (
      <Box
        component="img"
        src={finalImageUrl}
        alt={getResultTitle(result)}
        onError={() => {
          // If the image fails to load and we haven't already tried the default, use default
          if (!imageError && imageUrl && defaultImage) {
            setImageError(true);
          }
        }}
        sx={{
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          borderRadius: isCircular ? '50%' : 2,
          objectFit: isCircular ? 'cover' : 'contain',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: '#ffffff',
          transition: 'all 0.2s ease',
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
        }}
      />
    );
  };

  const renderResultImage = (result) => {
    return <ResultImage result={result} />;
  };

  const hasResults = useMemo(() => {
    return Object.values(results).some((arr) => arr.length > 0);
  }, [results]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Allow closing on mobile via backdrop click or Escape key
        // Also allow programmatic close (when reason is undefined or null)
        if (reason === 'backdropClick' || reason === 'escapeKeyDown' || !reason) {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={false}
      disableRestoreFocus={true}
      PaperProps={{
        sx: {
          borderRadius: { xs: '0', sm: '20px' },
          mt: { xs: 0, sm: 8 },
          mb: { xs: 0, sm: 4 },
          mx: { xs: 0, sm: 'auto' },
          maxHeight: { xs: '100vh', sm: '85vh' },
          height: { xs: '100vh', sm: 'auto' },
          width: { xs: '100%', sm: 'auto' },
          maxWidth: { xs: '100%', sm: '600px' },
          boxShadow: { xs: 'none', sm: '0px 24px 64px rgba(0, 0, 0, 0.15)' },
          overflow: 'hidden',
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
      BackdropProps={{
        sx: {
          backgroundColor: { xs: 'rgba(0, 0, 0, 0.7)', sm: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
    >
      <DialogContent 
        sx={{ 
          p: 0, 
          '&.MuiDialogContent-root': { 
            padding: 0, 
            position: 'relative',
            overflow: 'visible',
          } 
        }}
      >
        <Box 
          sx={{ 
            position: 'sticky',
            p: { xs: 1.5, sm: 3 },
            pt: { xs: 'max(12px, env(safe-area-inset-top, 12px))', sm: 3 },
            pr: { xs: 'max(12px, env(safe-area-inset-right, 12px))', sm: 3 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            top: 0,
            zIndex: 10,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', sm: 'stretch' },
          }}
        >
          {/* Search row: input + close button (side by side on mobile) */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Rechercher des experts, médicaments, actualités..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
            }}
            autoFocus
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5, flexShrink: 0 }}>
                  <Box
                    component="img"
                    src={searchIcon}
                    alt="Search"
                    sx={{ width: 22, height: 22, opacity: 0.6, filter: 'brightness(0)', transition: 'opacity 0.2s' }}
                  />
                </Box>
              ),
              endAdornment: (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end',
                    width: { xs: 'auto', sm: '90px' }, // Auto width on mobile to fit chip
                    minWidth: { xs: '24px', sm: '90px' },
                    height: { xs: '24px', sm: '24px' },
                    mr: { xs: 0.5, sm: 0.5 },
                    ml: { xs: 0.5, sm: 0 },
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 'auto',
                      opacity: loading || (searchTerm.length >= 2 && allResults.length > 0) ? 1 : 0,
                      visibility: loading || (searchTerm.length >= 2 && allResults.length > 0) ? 'visible' : 'hidden',
                      transition: 'opacity 0.15s ease, visibility 0.15s ease',
                    }}
                  >
                    {loading ? (
                <CircularProgress size={20} thickness={4} />
              ) : searchTerm.length >= 2 ? (
                <Chip
                  label={isMobile ? `${allResults.length}` : `${allResults.length} résultat${allResults.length > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ 
                    height: { xs: '22px', sm: '26px' }, 
                    fontSize: { xs: '11px', sm: '12px' },
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    whiteSpace: 'nowrap',
                    '& .MuiChip-label': { 
                      px: { xs: 1, sm: 1.5 },
                      lineHeight: { xs: '22px', sm: '26px' },
                      overflow: 'visible',
                      textOverflow: 'clip',
                    },
                  }}
                />
                    ) : null}
                  </Box>
                </Box>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '16px', sm: '16px' },
                borderRadius: { xs: '8px', sm: '12px' },
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : 'white',
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                willChange: 'auto', // Prevent layout shifts
                minHeight: { xs: '44px', sm: '52px' }, // Fixed height to prevent resizing
                height: { xs: '44px', sm: '52px' },
                boxSizing: 'border-box',
                '& .MuiInputBase-input': {
                  py: { xs: '10px', sm: '14px' }, // Fixed padding
                  fontSize: { xs: '16px', sm: '16px' }, // Prevent zoom on iOS
                },
                '& fieldset': {
                  borderWidth: { xs: '1px', sm: '1.5px' },
                  borderColor: alpha(theme.palette.divider, 0.3),
                  transition: 'border-color 0.2s ease',
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: { xs: '1.5px', sm: '2px' },
                },
              },
            }}
          />
            </Box>
            {isMobile && (
              <IconButton
                ref={closeButtonRef}
                onClick={handleClose}
                type="button"
                aria-label="Fermer la recherche"
                disableRipple
                sx={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  minHeight: 44,
                  flexShrink: 0,
                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.grey[700], 0.9) : alpha(theme.palette.grey[100], 0.9),
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                  borderRadius: 2,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : alpha(theme.palette.grey[200], 0.95),
                  },
                  '&:active': {
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : alpha(theme.palette.grey[300], 0.95),
                    transform: 'scale(0.96)',
                  },
                }}
              >
                <X size={22} strokeWidth={2.5} style={{ color: 'inherit' }} />
              </IconButton>
            )}
          </Box>
          {searchTerm.length >= 2 && (
            <Typography
              variant="caption"
              sx={{ 
                mt: { xs: 1, sm: 1.5 }, 
                display: { xs: 'none', sm: 'block' }, 
                color: 'text.secondary', 
                fontSize: { xs: '10px', sm: '11px' },
                opacity: 0.7,
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <kbd style={{ 
                  padding: '3px 8px', 
                  background: alpha(theme.palette.primary.main, 0.08), 
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  color: theme.palette.primary.main,
                }}>↑</kbd>
                <kbd style={{ 
                  padding: '3px 8px', 
                  background: alpha(theme.palette.primary.main, 0.08), 
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  color: theme.palette.primary.main,
                }}>↓</kbd>
                <span style={{ margin: '0 4px' }}>pour naviguer</span>
                <kbd style={{ 
                  padding: '3px 8px', 
                  background: alpha(theme.palette.primary.main, 0.08), 
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  color: theme.palette.primary.main,
                }}>Entrée</kbd>
                <span style={{ margin: '0 4px' }}>pour sélectionner</span>
                <kbd style={{ 
                  padding: '3px 8px', 
                  background: alpha(theme.palette.primary.main, 0.08), 
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  color: theme.palette.primary.main,
                }}>Esc</kbd>
                <span style={{ margin: '0 4px' }}>pour fermer</span>
              </Box>
            </Typography>
          )}
        </Box>

        <Box 
          ref={resultsContainerRef}
          sx={{ 
            maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(85vh - 140px)' }, 
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            // Prevent layout shifts and vibration
            contain: 'layout style paint',
            willChange: 'scroll-position',
            pb: { xs: 2, sm: 0 },
            '&::-webkit-scrollbar': {
              width: { xs: '4px', sm: '8px' },
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.divider, 0.3),
              borderRadius: '4px',
              '&:hover': {
                background: alpha(theme.palette.divider, 0.5),
              },
            },
          }}
        >
          {!searchTerm || searchTerm.length < 2 ? (
            <Box sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <Box
                component="img"
                src={searchIcon}
                alt="Search"
                sx={{ 
                  width: { xs: 48, sm: 64 }, 
                  height: { xs: 48, sm: 64 }, 
                  opacity: 0.3, 
                  mb: { xs: 2, sm: 3 }, 
                  filter: 'brightness(0)',
                  transition: 'opacity 0.3s ease',
                }}
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: { xs: '14px', sm: '15px' },
                }}
              >
                Tapez au moins 2 caractères pour rechercher
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  opacity: 0.6,
                  mt: 1,
                  display: 'block',
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 2, sm: 0 },
                }}
              >
                Recherchez parmi les experts, médicaments, actualités et plus encore
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <CircularProgress size={40} thickness={4} />
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2.5, 
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: { xs: '13px', sm: '14px' },
                }}
              >
                Recherche en cours...
              </Typography>
            </Box>
          ) : !hasResults ? (
            <Box sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  mb: 1,
                  fontSize: { xs: '14px', sm: '16px' },
                }}
              >
                Aucun résultat trouvé
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  opacity: 0.7,
                  fontSize: { xs: '12px', sm: '14px' },
                }}
              >
                Aucun résultat pour "{searchTerm}"
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {results.experts.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2.5, sm: 3 }, 
                      py: 1.5, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '11px', sm: '12px' },
                        letterSpacing: '1px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Experts
                    </Typography>
                    <Chip
                      label={results.experts.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.experts.map((item, idx) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'expert');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'expert' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'expert' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'expert' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                    opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'expert' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.medications.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Médicaments
                    </Typography>
                    <Chip
                      label={results.medications.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.medications.map((item, idx) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'medication');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'medication' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'medication' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'medication' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                    opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'medication' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.news.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Actualités
                    </Typography>
                    <Chip
                      label={results.news.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.news.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'news');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'news' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'news' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'news' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                    opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'news' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.library.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Bibliothèque
                    </Typography>
                    <Chip
                      label={results.library.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.library.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'library');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'library' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'library' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'library' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'library' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.opinions.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Avis Experts
                    </Typography>
                    <Chip
                      label={results.opinions.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.opinions.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'opinion');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'opinion' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: { xs: 48, sm: 56 }, 
                              height: { xs: 48, sm: 56 },
                              borderRadius: 2,
                              bgcolor: '#ffffff',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }}>
                              {getResultIcon('opinion')}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'opinion' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'opinion' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.institutions.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Institutions
                    </Typography>
                    <Chip
                      label={results.institutions.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.institutions.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'institution');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'institution' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'institution' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'institution' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'institution' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.formations.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Formations
                    </Typography>
                    <Chip
                      label={results.formations.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.formations.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'formation');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'formation' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: { xs: 48, sm: 56 }, 
                              height: { xs: 48, sm: 56 },
                              borderRadius: 2,
                              bgcolor: '#ffffff',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }}>
                              {getResultIcon('formation')}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'formation' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'formation' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.masterclasses.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Masterclass
                    </Typography>
                    <Chip
                      label={results.masterclasses.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.masterclasses.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'masterclass');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'masterclass' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: { xs: 48, sm: 56 }, 
                              height: { xs: 48, sm: 56 },
                              borderRadius: 2,
                              bgcolor: '#ffffff',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }}>
                              {getResultIcon('masterclass')}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'masterclass' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'masterclass' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.diseases.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Pathologies
                    </Typography>
                    <Chip
                      label={results.diseases.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.diseases.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'disease');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'disease' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: { xs: 48, sm: 56 }, 
                              height: { xs: 48, sm: 56 },
                              borderRadius: 2,
                              bgcolor: '#ffffff',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }}>
                              {getResultIcon('disease')}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'disease' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'disease' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              {results.events.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Événements
                    </Typography>
                    <Chip
                      label={results.events.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.events.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'event');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'event' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: { xs: 48, sm: 56 }, 
                              height: { xs: 48, sm: 56 },
                              borderRadius: 2,
                              bgcolor: '#ffffff',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }}>
                              {getResultIcon('event')}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'event' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'event' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </>
              )}

              {results.modules.length > 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: { xs: 2, sm: 2.5 }, 
                      py: { xs: 1, sm: 1.5 }, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '10px', sm: '11px' },
                        letterSpacing: '0.5px',
                        color: '#000000',
                        textTransform: 'uppercase',
                      }}
                    >
                      Modules
                    </Typography>
                    <Chip
                      label={results.modules.length}
                      size="small"
                      sx={{
                        height: { xs: '20px', sm: '22px' },
                        minWidth: { xs: '20px', sm: '22px' },
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: '#ffffff',
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 },
                        },
                      }}
                    />
                  </Box>
                  {results.modules.map((item) => {
                    const flatIndex = allResults.findIndex((r) => r.id === item.id && r.type === 'module');
                    const isSelected = flatIndex === selectedIndex;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          ref={isSelected ? selectedItemRef : null}
                          selected={isSelected}
                          onClick={() => handleSelectResult({ ...item, type: 'module' })}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.25, sm: 1.5 },
                            minHeight: { xs: '64px', sm: '72px' },
                            height: { xs: '64px', sm: '72px' },
                            borderRadius: '8px',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            mx: { xs: 1, sm: 1.5 },
                            my: 0.5,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[300], 0.4),
                              transform: { xs: 'none', sm: 'translateX(2px)' },
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.grey[300], 0.6),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 }, mr: { xs: 1.5, sm: 2 } }}>
                            {renderResultImage({ ...item, type: 'module' })}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: '#000000',
                                  fontSize: { xs: '14px', sm: '15px' },
                                  mb: 0.5,
                                }}
                              >
                                {getResultTitle({ ...item, type: 'module' })}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#000000',
                                  fontSize: { xs: '12px', sm: '13px' },
                                  opacity: 0.7,
                                }}
                              >
                                {getResultSubtitle({ ...item, type: 'module' })}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <ArrowRight 
                              size={18} 
                              style={{
                                color: '#000000',
                                opacity: isSelected ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                              }} 
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </>
              )}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;

